import { useScrollReveal } from "@/hooks/useScrollReveal";

interface ServiceAreasProps {
  title?: string;
  subtitle?: string;
  areas?: string[];
  showMap?: boolean;
}

const defaultAreas = [
  "Farmington, MN",
  "Apple Valley, MN",
  "Lakeville, MN",
  "Burnsville, MN",
  "Eagan, MN",
  "Rosemount, MN",
  "Prior Lake, MN",
  "Savage, MN",
  "Hastings, MN",
  "Northfield, MN",
  "Mendota Heights, MN",
  "Inver Grove Heights, MN",
  "West Saint Paul, MN",
  "South Saint Paul, MN",
  "Bloomington, MN",
  "Richfield, MN",
];

export default function ServiceAreas({
  title = "Serving Farmington & the Twin Cities South Metro",
  subtitle = "United Auto Group proudly serves customers from across Dakota County, Scott County, and the greater Twin Cities metropolitan area. Many customers drive over an hour to purchase from us.",
  areas = defaultAreas,
  showMap = true,
}: ServiceAreasProps) {
  const { ref: headerRef, isVisible: headerVisible } = useScrollReveal();
  const { ref: gridRef, isVisible: gridVisible } = useScrollReveal();
  const { ref: mapRef, isVisible: mapVisible } = useScrollReveal();

  return (
    <section className="bg-cream-DEFAULT py-20 md:py-28 overflow-hidden" aria-labelledby="areas-heading">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div
          ref={headerRef}
          className={`text-center mb-14 transition-all duration-700 ${
            headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <div className="inline-flex items-center gap-2 mb-5 justify-center">
            <span className="h-px w-10 bg-gold-DEFAULT" />
            <span className="text-gold-DEFAULT text-xs font-bold tracking-[0.22em] uppercase font-sans">
              SERVICE AREAS
            </span>
            <span className="h-px w-10 bg-gold-DEFAULT" />
          </div>
          <h2
            id="areas-heading"
            className="font-serif text-charcoal leading-tight max-w-2xl mx-auto mb-4"
            style={{ fontSize: "clamp(1.8rem, 3vw, 2.6rem)" }}
          >
            {title}
          </h2>
          <p className="text-charcoal-soft font-sans text-base max-w-xl mx-auto leading-relaxed">
            {subtitle}
          </p>
        </div>

        {showMap ? (
          /* ── Map layout ─────────────────────────────────────── */
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Map — prominent left panel */}
            <div
              ref={mapRef}
              className={`lg:w-1/2 transition-all duration-800 ${
                mapVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"
              }`}
            >
              {/* Map container with premium frame */}
              <div className="relative">
                <div className="w-full h-[440px] rounded-2xl overflow-hidden border-2 border-gold-DEFAULT/20">
                  <iframe
                    title="United Auto Group location map"
                    src="https://maps.google.com/maps?q=United+Auto+Group,+1024+8th+St,+Farmington,+MN+55024&output=embed"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
                {/* Floating top label */}
                <div className="absolute top-4 left-4 bg-charcoal/90 backdrop-blur-sm rounded-xl px-4 py-2.5 flex items-center gap-2.5">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-map-pin-fill text-gold-DEFAULT text-sm" />
                  </div>
                  <span className="text-white text-xs font-sans font-semibold whitespace-nowrap">
                    United Auto Group — Farmington, MN
                  </span>
                </div>
              </div>

              {/* Address + actions card */}
              <div className="bg-white rounded-2xl p-6 mt-4 flex flex-col sm:flex-row items-start sm:items-center gap-5 border border-cream-dark">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-gold-DEFAULT/12 flex-shrink-0">
                  <i className="ri-map-pin-2-fill text-gold-DEFAULT text-xl" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-charcoal font-sans text-sm mb-0.5">
                    1024 8th St, Farmington, MN 55024
                  </p>
                  <div className="flex flex-wrap items-center gap-3 mt-2">
                    <a
                      href="tel:9527693208"
                      className="text-gold-DEFAULT text-sm font-bold hover:text-gold-dark transition-colors cursor-pointer whitespace-nowrap"
                      aria-label="Call (952) 769-3208"
                    >
                      (952) 769-3208
                    </a>
                    <span className="text-charcoal-soft/30 text-xs">|</span>
                    <a
                      href="mailto:info@uagmn.com"
                      className="text-charcoal-soft text-sm hover:text-charcoal transition-colors cursor-pointer whitespace-nowrap"
                    >
                      info@uagmn.com
                    </a>
                  </div>
                </div>
                <a
                  href="https://maps.app.goo.gl/Vm3ubU5UPaCURLk67"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-charcoal text-white font-semibold text-xs px-5 py-3 rounded-full hover:bg-charcoal-light transition-colors cursor-pointer whitespace-nowrap flex-shrink-0"
                >
                  Get Directions
                  <div className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-map-pin-line text-xs" />
                  </div>
                </a>
              </div>
            </div>

            {/* City Grid — right panel */}
            <div
              ref={gridRef}
              className={`lg:w-1/2 flex flex-col gap-5 transition-all duration-800 ${
                gridVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"
              }`}
              style={{ transitionDelay: "120ms" }}
            >
              <div className="grid grid-cols-2 gap-3">
                {areas.map((area, i) => (
                  <div
                    key={area}
                    className={`bg-white rounded-xl px-4 py-3 flex items-center gap-2 hover:-translate-y-0.5 transition-all duration-200 cursor-default border border-cream-dark/60 ${
                      i === 0
                        ? "border-gold-DEFAULT/30 bg-gold-DEFAULT/5"
                        : ""
                    }`}
                  >
                    <div className="w-3 h-3 flex items-center justify-center flex-shrink-0">
                      <i
                        className={`text-xs ${
                          i === 0
                            ? "ri-map-pin-fill text-gold-DEFAULT"
                            : "ri-map-pin-2-fill text-gold-DEFAULT/60"
                        }`}
                      />
                    </div>
                    <span className="text-sm text-charcoal font-sans font-medium whitespace-nowrap">
                      {area}
                    </span>
                    {i === 0 && (
                      <span className="ml-auto text-[10px] font-bold text-gold-DEFAULT bg-gold-DEFAULT/10 px-2 py-0.5 rounded-full whitespace-nowrap">
                        Our Location
                      </span>
                    )}
                  </div>
                ))}
              </div>

              {/* Stats row */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-charcoal rounded-xl p-5 text-center">
                  <div className="font-serif text-gold-DEFAULT text-2xl font-semibold">60+</div>
                  <div className="text-white text-xs font-sans mt-1.5 leading-tight">
                    Vehicles In Stock
                  </div>
                </div>
                <div className="bg-charcoal rounded-xl p-5 text-center">
                  <div className="font-serif text-gold-DEFAULT text-2xl font-semibold">5.0★</div>
                  <div className="text-white text-xs font-sans mt-1.5 leading-tight">
                    Google Rating
                  </div>
                </div>
                <div className="bg-charcoal rounded-xl p-5 text-center">
                  <div className="font-serif text-gold-DEFAULT text-2xl font-semibold">15</div>
                  <div className="text-white text-xs font-sans mt-1.5 leading-tight">
                    Day Warranty
                  </div>
                </div>
              </div>

              {/* CTA */}
              <a
                href="https://www.uagmn.com/inventory/"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-gold-DEFAULT text-charcoal font-bold text-sm px-8 py-4 rounded-full hover:bg-gold-light transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                Browse Inventory for Your Area
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line" />
                </div>
              </a>
            </div>
          </div>
        ) : (
          /* ── No-map layout ──────────────────────────────────── */
          <div
            ref={gridRef}
            className={`transition-all duration-700 ${
              gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
          >
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-8">
              {areas.map((area) => (
                <div
                  key={area}
                  className="bg-white rounded-xl px-4 py-3 flex items-center gap-2 hover:-translate-y-0.5 transition-transform duration-200 cursor-default border border-cream-dark/60"
                >
                  <div className="w-3 h-3 flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-2-fill text-gold-DEFAULT text-xs" />
                  </div>
                  <span className="text-sm text-charcoal font-sans font-medium whitespace-nowrap">
                    {area}
                  </span>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-3 gap-4 max-w-lg mx-auto">
              <div className="bg-charcoal rounded-xl p-5 text-center">
                <div className="font-serif text-gold-DEFAULT text-2xl font-semibold">60+</div>
                <div className="text-white text-xs font-sans mt-1.5">Vehicles In Stock</div>
              </div>
              <div className="bg-charcoal rounded-xl p-5 text-center">
                <div className="font-serif text-gold-DEFAULT text-2xl font-semibold">5.0★</div>
                <div className="text-white text-xs font-sans mt-1.5">Google Rating</div>
              </div>
              <div className="bg-charcoal rounded-xl p-5 text-center">
                <div className="font-serif text-gold-DEFAULT text-2xl font-semibold">15-Day</div>
                <div className="text-white text-xs font-sans mt-1.5">Warranty</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
